// src/SignPage.js
// Client-facing agreement sign page.
// Handles password verification, signature/initials drawing,
// interactive sign/initial/text spots, and final submission.

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { buildPricingState, calcTotal, calcTotalYear, rowDisplayPrice, parsePrice, bankRowId, grandTotalsByIndex, fillPricingTotal } from "./shared/pricing";
import { buildOrder } from "./shared/order";

// ─── Drawing Canvas Component ─────────────────────────────────────────────────

function SignatureCanvas({ width = 400, height = 150, onSave, label, placeholder }) {
  const canvasRef  = useRef(null);
  const drawing    = useRef(false);
  const lastPos    = useRef(null);
  const [saved, setSaved] = useState(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top)  * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top)  * scaleY,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPos.current = getPos(e, canvasRef.current);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const pos    = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDraw = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
  };

  const save = () => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    const hasContent = Array.from(pixels).some((p, i) => i % 4 === 3 && p > 0);
    if (!hasContent) { alert('Please draw your ' + label + ' first.'); return; }
    // Flatten onto white before JPEG encoding (JPEG has no transparency channel)
    const flat = document.createElement('canvas');
    flat.width = canvas.width; flat.height = canvas.height;
    const fctx = flat.getContext('2d');
    fctx.fillStyle = '#ffffff';
    fctx.fillRect(0, 0, flat.width, flat.height);
    fctx.drawImage(canvas, 0, 0);
    onSave(flat.toDataURL('image/jpeg', 0.7));
    setSaved(true);
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>{label}</div>
      <div style={{ position: 'relative', border: '1.5px solid #ddd', borderRadius: 8, background: '#fafafa', overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          role="img"
          aria-label={`${label} drawing area — draw with mouse or touch`}
          style={{ display: 'block', width: '100%', cursor: 'crosshair', touchAction: 'none' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
        {placeholder && (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#ccc', fontSize: 13, pointerEvents: 'none', userSelect: 'none' }}>
            {placeholder}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button onClick={clear} style={btnStyle('#fff', '#ddd', '#555')}>Clear</button>
        <button onClick={save}  style={btnStyle('#1a3a6e', '#1a3a6e', '#fff')}>Save {label}</button>
      </div>
      {/* Screen-reader live region announces when saved */}
      <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
        {saved ? `${label} saved` : ''}
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function btnStyle(bg, border, color, extra = {}) {
  return { padding: '7px 16px', background: bg, border: `1px solid ${border}`, borderRadius: 6, color, fontSize: 13, cursor: 'pointer', fontFamily: 'Arial, sans-serif', ...extra };
}

function SpotButton({ filled, onClick, label, type, spotId, locked }) {
  const colors = {
    initial:   { bg: filled ? '#E8F5E9' : '#FFF9C4', border: filled ? '#2e7d32' : '#F9A825', text: filled ? '#2e7d32' : '#795548' },
    signature: { bg: filled ? '#E8F5E9' : '#FFF9C4', border: filled ? '#2e7d32' : '#F9A825', text: filled ? '#2e7d32' : '#795548' },
    text:      { bg: filled ? '#E8F5E9' : '#E3F2FD', border: filled ? '#2e7d32' : '#1565C0', text: filled ? '#2e7d32' : '#1565C0' },
  };
  const isLocked = locked && !filled;
  const c = colors[type] || colors.text;
  const displayText = filled ? '✓' : isLocked ? '🔒' : (type === 'initial' ? '[ initial ]' : type === 'signature' ? '[ sign here ]' : `[ ${label} ]`);
  const disabled = filled || isLocked;
  return (
    <button
      {...(!disabled ? { 'data-spotid': spotId } : {})}
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
      title={isLocked ? 'Initial the main product above to unlock this option' : undefined}
      aria-label={isLocked ? `${label} — locked until main product is initialed` : filled ? `${label} — filled` : `Fill ${label}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: isLocked ? '#f0f0f0' : c.bg, border: `1.5px solid ${isLocked ? '#ccc' : c.border}`, borderRadius: 4,
        color: isLocked ? '#aaa' : c.text, fontSize: 12, padding: '2px 8px',
        cursor: isLocked ? 'not-allowed' : filled ? 'default' : 'pointer',
        fontFamily: 'Arial, sans-serif', userSelect: 'none',
        // Reset native button styles
        appearance: 'none', lineHeight: 'inherit', verticalAlign: 'middle',
      }}
    >
      {displayText}
    </button>
  );
}

// ─── Text Prompt Modal ────────────────────────────────────────────────────────

function TextPrompt({ field, onSave, onClose }) {
  const [val, setVal]   = useState('');
  const inputRef        = useRef(null);
  const cancelRef       = useRef(null);

  const trapFocus = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key !== 'Tab') return;
    const focusable = [inputRef.current, cancelRef.current].filter(Boolean);
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
    } else {
      if (document.activeElement === last)  { e.preventDefault(); first?.focus(); }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="text-prompt-title"
      onKeyDown={trapFocus}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
    >
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        <div id="text-prompt-title" style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#1a3a6e' }}>Enter {field}</div>
        <label htmlFor="text-prompt-input" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>{field}</label>
        <input
          id="text-prompt-input"
          ref={inputRef}
          autoFocus
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && val.trim()) onSave(val.trim()); }}
          placeholder={`Type your ${field.toLowerCase()}…`}
          style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', marginBottom: 14 }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button ref={cancelRef} onClick={onClose} style={btnStyle('#fff', '#ddd', '#555')}>Cancel</button>
          <button onClick={() => val.trim() && onSave(val.trim())} style={btnStyle('#1a3a6e', '#1a3a6e', '#fff')}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main SignPage Component ──────────────────────────────────────────────────

export default function SignPage({ clientToken }) {
  useEffect(() => {
    document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const [stage, setStage]         = useState('password'); // password | setup | confirm | sign | done
  const [password, setPassword]   = useState('');
  const [eConsent, setEConsent]       = useState(false);
  const [paperConfirmMode, setPaperConfirmMode] = useState(false);
  const [pwError, setPwError]         = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [draft, setDraft]         = useState(null);

  // Signatures
  const [initials, setInitials]   = useState(null);
  const [signature, setSignature] = useState(null);

  // Spots tracking — key: spotId, value: filled data
  const [spots, setSpots]         = useState({});
  const [prompt, setPrompt]       = useState(null); // { spotId, field }

  // Confirm-details fields (pre-filled, remembered in localStorage)
  const [confirmFields, setConfirmFields] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);

  // Partial progress
  const [resumed, setResumed] = useState(false);
  const pendingProgressRef  = useRef(null); // partial spots to merge after buildSpots
  const progressDataRef     = useRef({});   // always-fresh snapshot for auto-save

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // ── Client comments (Google Docs-style) ─────────────────────────────────────
  const [pendingComments,    setPendingComments]    = useState([]);
  const [selectionAnchor,    setSelectionAnchor]    = useState(null); // { x, y, selectedText }
  const [commentPopover,     setCommentPopover]     = useState(null); // open popover
  const [commentPopoverText, setCommentPopoverText] = useState('');
  const [priorComments,      setPriorComments]      = useState([]);
  const [showPriorResponses, setShowPriorResponses] = useState(true);
  const [replyTexts,         setReplyTexts]         = useState({});
  const [savingReplyId,      setSavingReplyId]      = useState(null);
  const [resolvingCommentId, setResolvingCommentId] = useState(null);

  // ── Verify password ──────────────────────────────────────────────────────────
  const handlePasswordSubmit = async () => {
    if (!password.trim() || !eConsent) return;
    setPwLoading(true); setPwError('');
    try {
      const res  = await fetch(`/api/sign/${clientToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify', password: password.trim(), eConsent }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.expired) { setStage('expired'); return; }
        setPwError(data.error || 'Incorrect password');
        return;
      }
      setDraft(data.draft);
      setPriorComments(data.clientComments || []);

      // Client is waiting for team to respond to their submitted comments
      if (data.draftStatus === 'client_commented') {
        setStage('comments-waiting');
        return;
      }

      const pp = data.partialProgress;
      const hasFullProgress = pp && pp.initials && pp.signature;

      if (hasFullProgress) {
        // Full resume — skip setup and confirm, restore all saved state
        pendingProgressRef.current = pp;
        setInitials(pp.initials);
        setSignature(pp.signature);
        if (pp.confirmedFields) {
          setConfirmFields(pp.confirmedFields);
          localStorage.setItem(`sign_${clientToken}`, JSON.stringify(pp.confirmedFields));
        }
        setCurrentPage(pp.currentPage || 0);
        setResumed(true);
        setStage('sign');
      } else {
        // Fresh start (or partial progress without signatures — restore what we can)
        if (pp?.spots && Object.keys(pp.spots).length > 0) {
          pendingProgressRef.current = pp; // restore any filled spots after buildSpots runs
        }
        const stored = JSON.parse(localStorage.getItem(`sign_${clientToken}`) || '{}');
        const cf = pp?.confirmedFields;
        const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        setConfirmFields({
          printName: cf?.printName ?? stored.printName ?? '',
          title:     cf?.title     ?? stored.title     ?? '',
          date:      cf?.date      ?? stored.date      ?? today,
          addr1:     cf?.addr1     ?? stored.addr1     ?? '',
          addr2:     cf?.addr2     ?? stored.addr2     ?? '',
          apName:    cf?.apName    ?? stored.apName    ?? '',
          apPhone:   cf?.apPhone   ?? stored.apPhone   ?? '',
          apEmail:   cf?.apEmail   ?? stored.apEmail   ?? '',
        });
        setStage('setup');
      }
    } catch { setPwError('Could not connect. Please try again.'); }
    finally   { setPwLoading(false); }
  };

  const handlePaperRequest = async () => {
    try {
      const res = await fetch(`/api/sign/${clientToken}/request-paper`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({}),
      });
      if (res.ok) {
        setStage('paper-requested');
      } else {
        const data = await res.json().catch(() => ({}));
        setPwError(data.error || 'Something went wrong. Please try again.');
        setPaperConfirmMode(false);
      }
    } catch {
      setPwError('Network error. Please check your connection and try again.');
      setPaperConfirmMode(false);
    }
  };

  // ── Build list of required spots from sections ───────────────────────────────
  const buildSpots = useCallback((sections) => {
    const required = {};
    let spotIdx = 0;
    if (!sections) return required;
    for (const section of sections) {
      if (section.type === 'pricing_table') {
        // The table's "main/core product" is its required initial row (initial + !optional).
        // Optional add-ons in the same table are gated behind it (gateRowId) — they can't be
        // selected until the core is initialed, and clearing the core cascades to clear them.
        const coreRow = (section.rows || []).find(r => r.initial && !r.optional && !r.isTotal);
        const gateRowId = coreRow ? coreRow.id : null;
        for (const row of (section.rows || [])) {
          const isCore = !!(coreRow && row.id === coreRow.id);
          const gate = (row.optional && gateRowId) ? gateRowId : null;
          if (row.qty) {
            // Quantity row: one optional number spot + optional "unlimited" initial if row.unlimited
            const qtyId = `qty_${spotIdx++}`;
            required[qtyId] = { type: 'qty', label: `How many? (${row.module})`, module: row.module, rowId: row.id, gateRowId: gate, filled: false, value: null, declined: false, optional: true };
            if (row.unlimited) {
              const unlId = `unlimited_${spotIdx++}`;
              required[unlId] = { type: 'initial', label: `Initial for unlimited (${row.module})`, module: row.module, rowId: row.id, gateRowId: gate, isUnlimited: true, filled: false, value: null, declined: false, optional: true };
            }
          } else if (row.initial) {
            const id = `initial_${spotIdx++}`;
            // Core products are declinable too (isCore) — they don't individually block submit,
            // but at least one core across the agreement must be initialed (see allFilled).
            required[id] = { type: 'initial', label: 'Initial Here', module: row.module, rowId: row.id, gateRowId: gate, isCore, optional: isCore ? true : !!row.optional, filled: false, value: null, declined: false };
          }
        }
      }
      if (section.type === 'bank_of_hours') {
        const id = `bankhours_${spotIdx++}`;
        required[id] = { type: 'qty', label: 'Prepaid support hours', rowId: bankRowId(section), filled: false, value: null, optional: true };
      }
      if (section.type === 'signature') {
        const fields = [
          { key: `sig_sign_${spotIdx++}`,    type: 'signature', label: 'Client Signature' },
          { key: `sig_name_${spotIdx++}`,    type: 'text',      label: 'Print Name' },
          { key: `sig_title_${spotIdx++}`,   type: 'text',      label: 'Title' },
          { key: `sig_date_${spotIdx++}`,    type: 'text',      label: 'Date' },
          { key: `sig_addr1_${spotIdx++}`,   type: 'text',      label: 'Mailing Address Line 1' },
          { key: `sig_addr2_${spotIdx++}`,   type: 'text',      label: 'Mailing Address Line 2' },
          { key: `ap_name_${spotIdx++}`,     type: 'text',      label: 'A/P Contact Name' },
          { key: `ap_phone_${spotIdx++}`,    type: 'text',      label: 'A/P Phone' },
          { key: `ap_email_${spotIdx++}`,    type: 'text',      label: 'A/P Email' },
        ];
        for (const f of fields) required[f.key] = { type: f.type, label: f.label, filled: false, value: null };
      }
    }
    return required;
  }, []);

  // ── Split sections into pages — one per selected module, signature last ──────
  const signPages = useMemo(() => {
    const sections = draft?.sections || [];
    const hasModules = sections.some(s => s._module);

    const nonSig = sections.filter(s => s.type !== 'signature');
    // One signature for the whole (possibly multi-product) agreement — collapse any
    // duplicate signature blocks from a bundled snapshot down to a single final one.
    const allSig = sections.filter(s => s.type === 'signature');
    const sig    = allSig.length > 0 ? [allSig[allSig.length - 1]] : [];

    let pages;

    if (hasModules) {
      // Group consecutive sections by _module (Map preserves insertion order).
      // Section order within each group is preserved exactly as authored in the
      // template — do NOT reorder (e.g. pricing tables stay where they appear,
      // so totals/notes that follow a table render below it, not above it).
      const moduleMap = new Map();
      for (const section of nonSig) {
        const mod = section._module || 'Additional Terms';
        if (!moduleMap.has(mod)) moduleMap.set(mod, []);
        moduleMap.get(mod).push(section);
      }
      pages = Array.from(moduleMap.values());
    } else {
      // Fallback for old drafts without _module annotations
      pages = nonSig.length > 0 ? [nonSig] : [];
    }

    // The signature block lives on its own final page, so a trailing "Signatures:"
    // heading (plus any dangling spacers) at the end of a content page is redundant
    // on the sign page — strip it. (The PDF keeps it, since it's one continuous doc.)
    const stripTrailingSignatureHeader = (page) => {
      const out = [...page];
      while (out.length) {
        const last = out[out.length - 1];
        if (last.type === 'spacer') { out.pop(); continue; }
        if (last.type === 'heading' && /signature/i.test(last.content || '')) { out.pop(); continue; }
        break;
      }
      return out;
    };
    pages = pages.map(stripTrailingSignatureHeader).filter(p => p.length > 0);

    if (sig.length > 0) pages.push(sig);
    return pages;
  }, [draft]);

  useEffect(() => {
    if (!draft) return;
    const fresh = buildSpots(signPages.flat());
    const saved = pendingProgressRef.current?.spots;
    if (saved) {
      for (const [id, spot] of Object.entries(fresh)) {
        if (saved[id]?.filled) fresh[id] = { ...spot, filled: true, value: saved[id].value };
        else if (saved[id]?.declined) fresh[id] = { ...spot, declined: true };
      }
    }
    setSpots(fresh);
  }, [draft, signPages, buildSpots]);

  // Keep progress ref fresh for the auto-save effect
  useEffect(() => {
    progressDataRef.current = { password, spots, confirmedFields: confirmFields, currentPage, initials, signature };
  }, [password, spots, confirmFields, currentPage, initials, signature]);

  // Auto-save progress to server whenever spots change (debounced 1.5s)
  useEffect(() => {
    if (stage !== 'sign' || !password) return;
    const timer = setTimeout(async () => {
      const data = progressDataRef.current;
      try {
        await fetch(`/api/sign/${clientToken}/progress`, {
          method:  'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(data),
        });
      } catch {} // silent — don't interrupt the user
    }, 1500);
    return () => clearTimeout(timer);
  }, [spots, stage, clientToken, password]);

  const fillSpot = (id, value) => {
    setSpots(prev => ({ ...prev, [id]: { ...prev[id], filled: true, value, declined: false } }));
  };

  // Set a spot's accepted/declined state and, when it's no longer accepted, cascade-reset the
  // add-ons gated behind it (a dropped/declined product leaves no orphaned, still-priced options).
  const setSpotChoice = (id, patch) => {
    setSpots(prev => {
      const next = { ...prev, [id]: { ...prev[id], ...patch } };
      if (!patch.filled) {
        const rowId = prev[id]?.rowId;
        if (rowId) {
          for (const [k, s] of Object.entries(prev)) {
            if (k !== id && s.gateRowId && s.gateRowId === rowId) {
              next[k] = { ...s, filled: false, value: null, declined: false };
            }
          }
        }
      }
      return next;
    });
  };

  const clearSpot   = (id) => setSpotChoice(id, { filled: false, value: null, declined: false });
  const declineSpot = (id) => setSpotChoice(id, { filled: false, value: null, declined: true });
  const undoDecline = (id) => setSpotChoice(id, { filled: false, value: null, declined: false });

  // Live, always-editable quantity entry (no popup). Empty input clears the row.
  const handleQtyChange = (id, raw) => {
    const digits = String(raw).replace(/[^0-9]/g, '');
    const num = digits === '' ? null : parseInt(digits, 10);
    setSpots(prev => ({ ...prev, [id]: { ...prev[id], filled: num != null, value: num } }));
  };

  const handleSpotClick = (id) => {
    const spot = spots[id];
    if (spot.filled) {
      // Allow toggling an initials/unlimited stamp back off so it can be changed.
      if (spot.type === 'initial') clearSpot(id);
      return;
    }
    if (spot.type === 'initial')   { fillSpot(id, initials);  return; }
    if (spot.type === 'signature') { fillSpot(id, signature); return; }
    if (spot.type === 'text' || spot.type === 'qty') {
      setPrompt({ spotId: id, field: spot.label });
    }
  };

  // ── Google Docs-style text-selection commenting ──────────────────────────────
  const handleContentMouseUp = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) {
      setSelectionAnchor(null);
      return;
    }
    // Snap selection to word boundaries
    try {
      const range = sel.getRangeAt(0);
      if (range.startContainer.nodeType === Node.TEXT_NODE) {
        const t = range.startContainer.textContent;
        let s = range.startOffset;
        while (s > 0 && /\S/.test(t[s - 1])) s--;
        range.setStart(range.startContainer, s);
      }
      if (range.endContainer.nodeType === Node.TEXT_NODE) {
        const t = range.endContainer.textContent;
        let e = range.endOffset;
        // Trim any trailing whitespace the browser included (common on double-click)
        // before extending forward, so we don't bleed into the next word.
        while (e > 0 && /\s/.test(t[e - 1])) e--;
        while (e < t.length && /\S/.test(t[e])) e++;
        range.setEnd(range.endContainer, e);
      }
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (_) {}

    const selectedText = sel.toString().trim();
    if (selectedText.length < 2) { setSelectionAnchor(null); return; }

    const rect = sel.getRangeAt(0).getBoundingClientRect();
    setSelectionAnchor({ x: rect.right + 8, y: rect.top, selectedText: selectedText.slice(0, 200) });
  };

  const addPendingComment = () => {
    if (!commentPopoverText.trim()) return;
    setPendingComments(prev => [...prev, {
      sectionIdx:    currentPage,
      sectionPreview: commentPopover?.selectedText || 'Agreement',
      text:           commentPopoverText.trim(),
    }]);
    setCommentPopoverText('');
    setCommentPopover(null);
    window.getSelection()?.removeAllRanges();
  };

  // ── Confirm details → auto-fill text spots ────────────────────────────────────
  const handleConfirm = () => {
    localStorage.setItem(`sign_${clientToken}`, JSON.stringify(confirmFields));
    const labelMap = {
      'Print Name':             confirmFields.printName,
      'Title':                  confirmFields.title,
      'Date':                   confirmFields.date,
      'Mailing Address Line 1': confirmFields.addr1,
      'Mailing Address Line 2': confirmFields.addr2,
      'A/P Contact Name':       confirmFields.apName,
      'A/P Phone':              confirmFields.apPhone,
      'A/P Email':              confirmFields.apEmail,
    };
    const newSpots = { ...spots };
    for (const [id, spot] of Object.entries(newSpots)) {
      const val = labelMap[spot.label];
      if (val && val.trim())                              newSpots[id] = { ...spot, filled: true, value: val.trim() };
      if (spot.type === 'initial'   && spot.filled && initials)  newSpots[id] = { ...spot, filled: true, value: initials };
      if (spot.type === 'signature' && spot.filled && signature) newSpots[id] = { ...spot, filled: true, value: signature };
    }
    setSpots(newSpots);
    setCurrentPage(0);
    setStage('sign');

    // Save progress immediately after confirm so a page refresh resumes here
    fetch(`/api/sign/${clientToken}/progress`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ password, spots: newSpots, confirmedFields: confirmFields, currentPage: 0, initials, signature }),
    }).catch(() => {});
  };

  const getPageTitle = (page) => {
    if (!page?.length) return '';
    if (page.some(s => s.type === 'signature')) return 'Summary & Signature';
    const m = page.find(s => s._module);
    if (m) return m._module;
    const h = page.find(s => s.type === 'heading');
    if (h) return fill(h.content, draft?.fields || {});
    return 'Agreement';
  };

  // Every required spot (signature block) filled, AND — if the agreement has core products —
  // at least one product initialed. Optional add-ons and declined/blank products never block.
  const coreSpots = Object.values(spots).filter(s => s.isCore);
  const atLeastOneProduct = coreSpots.length === 0 || coreSpots.some(s => s.filled);
  const allFilled = Object.values(spots).every(s => s.filled || s.optional) && atLeastOneProduct;

  // ── Jump to next unfilled spot ────────────────────────────────────────────────
  const jumpToNextSpot = () => {
    // Look for an unfilled spot visible in the DOM (current page)
    const el = document.querySelector('[data-spotid]');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Brief highlight pulse
      el.style.outline = '3px solid #F9A825';
      setTimeout(() => { el.style.outline = ''; }, 1200);
      return;
    }
    // No unfilled spot on current page — advance to next page
    setCurrentPage(p => {
      const next = p + 1;
      if (next < signPages.length) { window.scrollTo(0, 0); return next; }
      return p;
    });
  };

  // Count unfilled spots on the current page only (for button label)
  const unfilledOnPage = (() => {
    const pageSpotKeys = new Set();
    const pageSections = signPages[currentPage] || [];
    let idx = signPages.slice(0, currentPage).flat().reduce((s, sec) => s + countSectionKeys(sec), 0);
    const allKeys = Object.keys(spots);
    for (const sec of pageSections) {
      const n = countSectionKeys(sec);
      for (let i = 0; i < n; i++) { const k = allKeys[idx + i]; if (k) pageSpotKeys.add(k); }
      idx += n;
    }
    return [...pageSpotKeys].filter(k => spots[k] && !spots[k].filled && !spots[k].optional).length;
  })();

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true); setSubmitError('');
    try {
      const signerName = Object.values(spots).find(s => s.label === 'Print Name')?.value || 'Client';
      const res = await fetch(`/api/sign/${clientToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:      'submit',
          password:    password.trim(),
          signerName,
          signature:   signature,
          initials:    initials,
          spots:       Object.fromEntries(Object.entries(spots).map(([k, v]) => [k, { label: v.label, value: v.value, type: v.type, filled: v.filled, declined: v.declined, rowId: v.rowId, isUnlimited: v.isUnlimited }])),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setSubmitError(data.error || 'Submission failed'); return; }
      setStage('done');
    } catch { setSubmitError('Could not submit. Please try again.'); }
    finally   { setSubmitting(false); }
  };

  // ── Submit comments to team ──────────────────────────────────────────────────
  const handleSubmitComments = async () => {
    if (pendingComments.length === 0) return;
    setSubmitting(true); setSubmitError('');
    try {
      const res = await fetch(`/api/sign/${clientToken}/comments`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          password: password.trim(),
          comments: pendingComments.map(c => ({ sectionIdx: c.sectionIdx, sectionPreview: c.sectionPreview, text: c.text })),
        }),
      });
      const d = await res.json();
      if (!res.ok) { setSubmitError(d.error || 'Failed to submit'); return; }
      setPendingComments([]);
      setCommentPopover(null);
      setStage('comments-submitted');
    } catch { setSubmitError('Could not submit. Please try again.'); }
    finally   { setSubmitting(false); }
  };

  // ── Render: Paper copy requested ─────────────────────────────────────────────
  if (stage === 'paper-requested') {
    return (
      <div style={pageWrap}>
        <div style={card(480)}>
          <Logo />
          <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📬</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: '#1a3a6e', marginBottom: 12 }}>
              Request received
            </div>
            <p style={{ fontSize: 14, color: '#444', lineHeight: 1.7, margin: '0 0 8px' }}>
              Someone from Dynamic Health IT will be in touch with next steps.
            </p>
            <p style={{ fontSize: 13, color: '#767676', margin: 0 }}>
              You can close this window.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Expired link ─────────────────────────────────────────────────────
  if (stage === 'expired') {
    return (
      <div style={pageWrap}>
        <div style={card(460)}>
          <Logo />
          <div style={{ fontSize: 36, marginBottom: 12 }}>⏱</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1a3a6e', marginBottom: 12 }}>Sign Link Expired</div>
          <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, marginBottom: 20 }}>
            This sign link is no longer active. Sign links expire after 60 days for security.
          </p>
          <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7 }}>
            Please contact Dynamic Health IT to request a new link:
          </p>
          <p style={{ margin: '12px 0 0' }}>
            <a href="mailto:info@DynamicHealthIT.com" style={{ color: '#1a3a6e', fontWeight: 600 }}>
              info@DynamicHealthIT.com
            </a>
            &nbsp;&nbsp;·&nbsp;&nbsp;(504) 309-9103
          </p>
        </div>
      </div>
    );
  }

  // ── Render: Comments submitted (just submitted this session) ─────────────────
  if (stage === 'comments-submitted') {
    return (
      <div style={pageWrap}>
        <div style={{ ...card(480), textAlign: 'center' }}>
          <Logo />
          <div style={{ fontSize: 48, marginBottom: 16 }}>💬</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: '#795548', marginBottom: 10 }}>Comments submitted</div>
          <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, marginBottom: 12 }}>
            The Dynamic Health IT team has been notified and will respond to your comments.
          </p>
          <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7 }}>
            You'll receive an email when they've responded — then you can return here to review and sign.
          </p>
          <div style={{ marginTop: 24, padding: '12px 16px', background: '#fff8e1', borderRadius: 8, fontSize: 12, color: '#795548' }}>
            You can close this window.
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Waiting for team to respond / IM thread view ─────────────────────
  if (stage === 'comments-waiting') {
    const migrateCmt = (c) => {
      if (c.thread) return c;
      const thread = [{ from: 'client', text: c.text || '', at: c.createdAt }];
      if (c.teamResponse) thread.push({ from: 'team', text: c.teamResponse, at: c.respondedAt, by: c.respondedBy });
      return { ...c, thread, resolved: !!c.teamResponse, resolvedBy: c.teamResponse ? 'team' : null, resolvedAt: c.teamResponse ? c.respondedAt : null };
    };
    const normComments = priorComments.map(migrateCmt);
    const allResolved  = normComments.length > 0 && normComments.every(c => c.resolved);
    const fmtTime = (at) => at ? new Date(at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

    const handleClientReply = async (commentId) => {
      const text = (replyTexts[commentId] || '').trim();
      if (!text) return;
      setSavingReplyId(commentId);
      try {
        const res = await fetch(`/api/sign/${clientToken}/comments/${commentId}/reply`, {
          method:  'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ password, text }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed');
        setPriorComments(prev => prev.map(c => {
          if (c.id !== commentId) return c;
          const m = migrateCmt(c);
          return { ...m, thread: [...m.thread, { from: 'client', text, at: new Date().toISOString() }], resolved: false };
        }));
        setReplyTexts(prev => { const n = { ...prev }; delete n[commentId]; return n; });
      } catch (e) { /* silent */ }
      finally { setSavingReplyId(null); }
    };

    const handleClientResolve = async (commentId) => {
      setResolvingCommentId(commentId);
      try {
        const res = await fetch(`/api/sign/${clientToken}/comments/${commentId}/resolve`, {
          method:  'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ password }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed');
        const { allResolved: ar } = await res.json().catch(() => ({}));
        setPriorComments(prev => prev.map(c => c.id === commentId
          ? { ...migrateCmt(c), resolved: true, resolvedBy: 'client', resolvedAt: new Date().toISOString() }
          : c
        ));
        if (ar) setStage('setup');
      } catch (e) { /* silent */ }
      finally { setResolvingCommentId(null); }
    };

    return (
      <div style={pageWrap}>
        <div style={{ ...card(580), padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
          <Logo />
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>{allResolved ? '✓' : '💬'}</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#1a3a6e', marginBottom: 6 }}>
              {allResolved ? 'All comments resolved' : 'Agreement comments'}
            </div>
            <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6, margin: 0 }}>
              {allResolved
                ? 'You can now proceed to sign the agreement.'
                : 'Reply to the DHIT team or mark each comment as resolved when satisfied.'}
            </p>
          </div>

          {allResolved && (
            <button
              onClick={() => setStage('setup')}
              style={{ display: 'block', width: '100%', margin: '0 0 20px', padding: '12px 0', background: '#1a6e3c', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >
              Continue to Sign →
            </button>
          )}

          {normComments.map((c, idx) => {
            const thread    = c.thread || [];
            const replyText = replyTexts[c.id] || '';
            return (
              <div key={c.id} style={{ border: `1.5px solid ${c.resolved ? '#c8e6c9' : '#fcd34d'}`, borderRadius: 8, marginBottom: 12, overflow: 'hidden', background: c.resolved ? '#f9fff9' : '#fff' }}>
                {/* Comment header */}
                <div style={{ padding: '8px 12px', background: c.resolved ? '#e8f5e9' : '#fff8e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: c.resolved ? '#2e7d32' : '#b45309' }}>
                    {c.resolved ? '✓ Resolved' : `Comment ${idx + 1}`}
                  </span>
                  {c.resolved && c.resolvedBy && (
                    <span style={{ fontSize: 11, color: '#888' }}>by {c.resolvedBy}</span>
                  )}
                </div>
                {/* Section anchor */}
                {c.sectionPreview && (
                  <div style={{ padding: '5px 12px', borderLeft: '3px solid #F9A825', margin: '6px 12px', fontSize: 11, color: '#888', fontStyle: 'italic', lineHeight: 1.4 }}>
                    &ldquo;{c.sectionPreview.slice(0, 80)}{c.sectionPreview.length > 80 ? '…' : ''}&rdquo;
                  </div>
                )}
                {/* Thread bubbles */}
                <div style={{ padding: '8px 12px' }}>
                  {thread.map((msg, mi) => (
                    <div key={mi} style={{ display: 'flex', justifyContent: msg.from === 'client' ? 'flex-start' : 'flex-end', marginBottom: 8 }}>
                      <div style={{
                        maxWidth: '85%',
                        background: msg.from === 'client' ? '#fff3e0' : '#e8f5e9',
                        border: `1px solid ${msg.from === 'client' ? '#ffe0b2' : '#c8e6c9'}`,
                        borderRadius: msg.from === 'client' ? '12px 12px 12px 2px' : '12px 12px 2px 12px',
                        padding: '7px 10px',
                      }}>
                        {msg.from === 'team' && msg.by && (
                          <div style={{ fontSize: 10, color: '#2e7d32', fontWeight: 600, marginBottom: 2 }}>{msg.by}</div>
                        )}
                        <div style={{ fontSize: 13, color: '#333', lineHeight: 1.5, wordBreak: 'break-word' }}>{msg.text}</div>
                        <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>{fmtTime(msg.at)}</div>
                      </div>
                    </div>
                  ))}
                  {/* Reply area (only for open comments) */}
                  {!c.resolved && (
                    <>
                      <textarea
                        value={replyText}
                        onChange={e => setReplyTexts(prev => ({ ...prev, [c.id]: e.target.value }))}
                        placeholder="Reply to this comment…"
                        rows={2}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1.5px solid #e0e0e0', borderRadius: 6, fontSize: 13, resize: 'vertical', fontFamily: 'Arial, sans-serif', marginTop: 4, marginBottom: 6 }}
                      />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => handleClientResolve(c.id)}
                          disabled={resolvingCommentId === c.id}
                          style={{ padding: '6px 10px', background: '#fff', border: '1px solid #ccc', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#555', cursor: 'pointer', flexShrink: 0, opacity: resolvingCommentId === c.id ? 0.5 : 1 }}
                        >
                          {resolvingCommentId === c.id ? '…' : '✓ Mark Resolved'}
                        </button>
                        <button
                          onClick={() => handleClientReply(c.id)}
                          disabled={!replyText.trim() || savingReplyId === c.id}
                          style={{ flex: 1, padding: '6px 10px', background: '#1a3a6e', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer', opacity: !replyText.trim() || savingReplyId === c.id ? 0.5 : 1 }}
                        >
                          {savingReplyId === c.id ? 'Sending…' : 'Send Reply →'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {!allResolved && (
            <div style={{ fontSize: 12, color: '#aaa', textAlign: 'center', marginTop: 8 }}>
              Once all comments are resolved you can proceed to sign.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Render: Password screen ───────────────────────────────────────────────────
  if (stage === 'password') {
    return (
      <div style={pageWrap}>
        <div style={card(420)}>
          <Logo />
          <div style={{ fontSize: 13, color: '#767676', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 28 }}>Agreement Sign Portal</div>

          {paperConfirmMode ? (
            <>
              <div style={{ background: '#fff8f3', border: '1px solid #f0d9c8', borderRadius: 8, padding: 18, marginBottom: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#1a3a6e', marginBottom: 10 }}>Request a paper copy</div>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: '#444', lineHeight: 1.6 }}>
                  No problem — someone from Dynamic Health IT will reach out with a physical copy.
                  You won't need to sign online.
                </p>
                <button
                  onClick={handlePaperRequest}
                  style={{ ...btnStyle('#e07b3f', '#e07b3f', '#fff'), width: '100%', padding: 11, fontSize: 15 }}
                >
                  Yes, request paper copy
                </button>
              </div>
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <button
                  onClick={() => setPaperConfirmMode(false)}
                  style={{ background: 'none', border: 'none', color: '#767676', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                >
                  Never mind, go back
                </button>
              </div>
            </>
          ) : (
            <>
              <label htmlFor="access-code" style={{ fontSize: 13, fontWeight: 500, color: '#444', marginBottom: 6, display: 'block' }}>Access code</label>
              <input
                id="access-code"
                type="password"
                value={password}
                autoComplete="one-time-code"
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handlePasswordSubmit()}
                placeholder="Enter the code from your email"
                style={inputStyle}
              />
              {pwError && <div role="alert" style={errorBox}>{pwError}</div>}

              <div style={{ margin: '4px 0 16px', padding: '14px', background: '#f8f9ff', border: '1px solid #d8dcee', borderRadius: 8 }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={eConsent}
                    onChange={e => setEConsent(e.target.checked)}
                    style={{ marginTop: 2, flexShrink: 0, accentColor: '#1a3a6e', width: 15, height: 15 }}
                  />
                  <span style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>
                    I consent to conduct this transaction electronically and to sign this agreement using an electronic signature.
                    I understand my electronic signature is legally binding, and that I may request a paper copy of any signed
                    document by contacting Dynamic Health IT at{' '}
                    <a href="mailto:info@DynamicHealthIT.com" style={{ color: '#1a3a6e' }}>info@DynamicHealthIT.com</a>.
                    {' '}To access and retain your signed agreement you will need a modern web browser and an email client
                    capable of opening PDF attachments.
                  </span>
                </label>
              </div>

              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <button
                  onClick={() => setPaperConfirmMode(true)}
                  style={{ background: 'none', border: 'none', color: '#767676', fontSize: 12, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                >
                  Prefer a paper copy?
                </button>
              </div>

              <button onClick={handlePasswordSubmit} disabled={pwLoading || !password || !eConsent} style={{ ...btnStyle('#1a3a6e', '#1a3a6e', '#fff'), width: '100%', padding: 11, fontSize: 15, marginTop: 4, opacity: (!password || !eConsent || pwLoading) ? 0.5 : 1 }}>
                {pwLoading ? 'Verifying…' : 'Access Agreement →'}
              </button>
              <div style={{ fontSize: 12, color: '#767676', marginTop: 20, textAlign: 'center' }}>Your access code was sent in a separate email from Dynamic Health IT.</div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Render: Setup signature + initials ────────────────────────────────────────
  if (stage === 'setup') {
    const ready = initials && signature;
    return (
      <div style={pageWrap}>
        <div style={card(520)}>
          <Logo />
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1a3a6e', marginBottom: 4 }}>Create your signature</div>
          <div style={{ fontSize: 13, color: '#767676', marginBottom: 24 }}>Draw both below. They will be saved and used throughout the agreement.</div>
          <SignatureCanvas width={500} height={110} label="Initials"  placeholder="Draw your initials here" onSave={setInitials} />
          <SignatureCanvas width={500} height={150} label="Signature" placeholder="Draw your full signature here" onSave={setSignature} />
          {initials  && <div role="status" style={{ color: '#2e7d32', fontSize: 12, marginBottom: 4 }}>✓ Initials saved</div>}
          {signature && <div role="status" style={{ color: '#2e7d32', fontSize: 12, marginBottom: 12 }}>✓ Signature saved</div>}
          <button
            disabled={!ready}
            onClick={() => setStage('confirm')}
            style={{ ...btnStyle('#1a3a6e', '#1a3a6e', '#fff'), width: '100%', padding: 11, fontSize: 15, opacity: ready ? 1 : 0.4 }}
          >
            Continue →
          </button>
        </div>
      </div>
    );
  }

  // ── Render: Confirm your details ──────────────────────────────────────────────
  if (stage === 'confirm' && confirmFields) {
    const cf = confirmFields;
    const set = (key) => (e) => setConfirmFields(p => ({ ...p, [key]: e.target.value }));
    const ready = cf.printName.trim() && cf.date.trim();
    return (
      <div style={{ ...pageWrap, alignItems: 'flex-start', paddingTop: 40 }}>
        <div style={card(560)}>
          <Logo />
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1a3a6e', marginBottom: 4 }}>Confirm your details</div>
          <div style={{ fontSize: 13, color: '#767676', marginBottom: 24 }}>
            We've pre-filled what we have. Review and update anything that's incorrect — we'll remember it for next time.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <div>
              <label htmlFor="cf-name" style={confirmLabel}>Print Name <span style={{ color: '#c62828' }} aria-hidden="true">*</span><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>(required)</span></label>
              <input id="cf-name" value={cf.printName} onChange={set('printName')} style={inputStyle} placeholder="Your full name" autoComplete="name" />
            </div>
            <div>
              <label htmlFor="cf-title" style={confirmLabel}>Title</label>
              <input id="cf-title" value={cf.title} onChange={set('title')} style={inputStyle} placeholder="e.g. CEO, Director" autoComplete="organization-title" />
            </div>
            <div>
              <label htmlFor="cf-date" style={confirmLabel}>Date <span style={{ color: '#c62828' }} aria-hidden="true">*</span><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>(required)</span></label>
              <input id="cf-date" value={cf.date} onChange={set('date')} style={inputStyle} placeholder="Today's date" />
            </div>
          </div>

          <label htmlFor="cf-addr1" style={{ ...confirmLabel, marginTop: 8 }}>Mailing Address Line 1</label>
          <input id="cf-addr1" value={cf.addr1} onChange={set('addr1')} style={inputStyle} placeholder="Street address" autoComplete="address-line1" />
          <label htmlFor="cf-addr2" style={confirmLabel}>Mailing Address Line 2</label>
          <input id="cf-addr2" value={cf.addr2} onChange={set('addr2')} style={inputStyle} placeholder="City, State ZIP" autoComplete="address-line2" />

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #eee' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 4 }}>A/P Contact</div>
            <div style={{ fontSize: 12, color: '#767676', marginBottom: 12 }}>Who handles accounts payable at your organization?</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <div>
                <label htmlFor="cf-apname" style={confirmLabel}>Contact Name</label>
                <input id="cf-apname" value={cf.apName} onChange={set('apName')} style={inputStyle} placeholder="Full name" autoComplete="name" />
              </div>
              <div>
                <label htmlFor="cf-apphone" style={confirmLabel}>Phone</label>
                <input id="cf-apphone" value={cf.apPhone} onChange={set('apPhone')} style={inputStyle} placeholder="(555) 555-5555" autoComplete="tel" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="cf-apemail" style={confirmLabel}>Email</label>
                <input id="cf-apemail" value={cf.apEmail} onChange={set('apEmail')} style={inputStyle} placeholder="ap@yourcompany.com" autoComplete="email" />
              </div>
            </div>
          </div>

          <button
            disabled={!ready}
            onClick={handleConfirm}
            style={{ ...btnStyle('#1a3a6e', '#1a3a6e', '#fff'), width: '100%', padding: 11, fontSize: 15, marginTop: 24, opacity: ready ? 1 : 0.4 }}
          >
            Looks Good, Continue to Agreement →
          </button>
          <button
            onClick={() => { setInitials(null); setSignature(null); setStage('setup'); }}
            style={{ background: 'none', border: 'none', color: '#888', fontSize: 13, cursor: 'pointer', marginTop: 12, width: '100%', textDecoration: 'underline' }}
          >
            Change signature / initials
          </button>
        </div>
      </div>
    );
  }

  // ── Render: Done ─────────────────────────────────────────────────────────────
  if (stage === 'done') {
    return (
      <div style={pageWrap}>
        <div style={{ ...card(480), textAlign: 'center' }}>
          <Logo />
          <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: '#1a6e3c', marginBottom: 10 }}>Agreement signed successfully</div>
          <div style={{ fontSize: 14, color: '#666', lineHeight: 1.6 }}>
            Thank you, <strong>{draft?.fields?.CUSTOMER_NAME || 'valued client'}</strong>.<br />
            A signed PDF copy has been sent to your email address.<br />
            Dynamic Health IT will be in touch shortly.
          </div>
          <div style={{ marginTop: 24, padding: '12px 16px', background: '#f4f5f7', borderRadius: 8, fontSize: 12, color: '#767676' }}>
            Keep this page open or save the confirmation email for your records.
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Sign page (paginated) ─────────────────────────────────────────────
  if (stage === 'sign' && signPages.length === 0 && draft) {
    return (
      <div style={pageWrap}>
        <div style={card(480)}>
          <Logo />
          <div style={{ fontSize: 15, color: '#c62828', fontWeight: 600, marginBottom: 8 }}>Document not available</div>
          <div style={{ fontSize: 13, color: '#666' }}>This agreement could not be loaded. Please contact Dynamic Health IT at <a href="mailto:info@DynamicHealthIT.com">info@DynamicHealthIT.com</a>.</div>
        </div>
      </div>
    );
  }

  const requiredSpots = Object.values(spots).filter(s => !s.optional);
  const filledCount  = requiredSpots.filter(s => s.filled).length;
  const totalCount   = requiredSpots.length;
  const totalPages   = signPages.length;
  const isLastPage   = currentPage === totalPages - 1;
  const pageTitle    = getPageTitle(signPages[currentPage]);

  // Compute how many spot keys precede this page's sections
  const pageKeyOffset = signPages
    .slice(0, currentPage)
    .flat()
    .reduce((sum, s) => sum + countSectionKeys(s), 0);

  return (
    <div style={{ minHeight: '100vh', background: '#f4f5f7', fontFamily: 'Arial, sans-serif' }}>

      {/* Sticky header */}
      <div style={{ position: 'sticky', top: 0, background: '#1a3a6e', color: '#fff', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 100, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Agreement — {draft?.fields?.CUSTOMER_NAME}</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>{filledCount} of {totalCount} spots completed</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 12, opacity: 0.8 }}>{allFilled ? 'All spots complete' : `${totalCount - filledCount} remaining`}</div>
          <button
            onClick={() => setStage('confirm')}
            style={{ padding: '8px 14px', background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 6, color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
          >
            Edit Info & Signature
          </button>
          {pendingComments.length > 0 ? (
            <button
              onClick={handleSubmitComments}
              disabled={submitting}
              style={{ padding: '8px 20px', background: '#e07b3f', border: 'none', borderRadius: 6, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              {submitting ? 'Submitting…' : `Submit ${pendingComments.length} Comment${pendingComments.length !== 1 ? 's' : ''} →`}
            </button>
          ) : (
            <button
              disabled={!allFilled || submitting}
              onClick={handleSubmit}
              style={{ padding: '8px 20px', background: allFilled ? '#1a6e3c' : '#555', border: 'none', borderRadius: 6, color: '#fff', fontSize: 14, fontWeight: 600, cursor: allFilled ? 'pointer' : 'not-allowed' }}
            >
              {submitting ? 'Submitting…' : 'Submit & Sign →'}
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div
        role="progressbar"
        aria-valuenow={totalCount ? Math.round((filledCount / totalCount) * 100) : 0}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${filledCount} of ${totalCount} signature spots completed`}
        style={{ height: 4, background: '#ddd' }}
      >
        <div style={{ height: '100%', background: '#1D9E75', width: `${totalCount ? (filledCount / totalCount) * 100 : 0}%`, transition: 'width 0.3s' }} />
      </div>

      {/* Page indicator strip */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto' }}>
        {signPages.map((page, i) => (
          <button
            key={i}
            onClick={() => setCurrentPage(i)}
            aria-current={i === currentPage ? 'page' : undefined}
            style={{
              padding: '4px 12px', borderRadius: 20, border: 'none', fontSize: 12, whiteSpace: 'nowrap', cursor: 'pointer',
              background: i === currentPage ? '#1a3a6e' : '#f0f2f5',
              color: i === currentPage ? '#fff' : '#444',
              fontWeight: i === currentPage ? 600 : 400,
            }}
          >
            {i + 1}. {getPageTitle(page)}
          </button>
        ))}
      </div>

      {/* Instructions banner */}
      <div style={{ background: '#FFF9C4', borderBottom: '1px solid #F9A825', padding: '10px 24px', fontSize: 13, color: '#795548' }}>
        <strong>How to sign:</strong> Click each highlighted spot to fill it. Yellow = needs attention. Green = complete.
      </div>

      {/* Prior round responses — shown when team has already answered previous comments */}
      {priorComments.length > 0 && priorComments.some(c => c.teamResponse) && showPriorResponses && (
        <div style={{ maxWidth: 860, margin: '16px auto 0', padding: '0 16px' }}>
          <div style={{ background: '#f0faf4', border: '1px solid #a5d6a7', borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#2e7d32' }}>
                ✓ DHIT responded to your previous comments
              </div>
              <button
                onClick={() => setShowPriorResponses(false)}
                style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1 }}
              >✕</button>
            </div>
            {priorComments.filter(c => c.teamResponse).map((c, i) => (
              <div key={i} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: i < priorComments.filter(x => x.teamResponse).length - 1 ? '1px solid #c8e6c9' : 'none' }}>
                {c.sectionPreview && (
                  <div style={{ fontSize: 11, color: '#aaa', fontStyle: 'italic', marginBottom: 4 }}>
                    Re: "{c.sectionPreview.slice(0, 60)}{c.sectionPreview.length > 60 ? '…' : ''}"
                  </div>
                )}
                <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>You:</span> {c.text}
                </div>
                <div style={{ fontSize: 13, color: '#1a3a6e', background: '#e8f5e9', borderRadius: 4, padding: '6px 10px' }}>
                  <span style={{ fontWeight: 600 }}>DHIT ({c.respondedBy || 'Team'}):</span> {c.teamResponse}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {resumed && (
        <div role="status" style={{ background: '#E8F5E9', borderBottom: '1px solid #A5D6A7', padding: '10px 24px', fontSize: 13, color: '#2e7d32' }}>
          ✓ Welcome back — your progress has been restored.
        </div>
      )}

      {submitError && <div role="alert" style={{ ...errorBox, margin: '12px 24px' }}>{submitError}</div>}

      {/* Current page content */}
      <div style={{ maxWidth: 1100, margin: '24px auto', padding: '0 16px', display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }} onMouseUp={handleContentMouseUp}>
        {pageTitle && (
          <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
            Section {currentPage + 1} of {totalPages} — <strong style={{ color: '#1a3a6e' }}>{pageTitle}</strong>
          </div>
        )}

        {/* Pricing summary card — shown on the signature page.
            Itemized from the same buildOrder() the signed order is frozen from,
            so what the client reads here is exactly what gets recorded. */}
        {isLastPage && (() => {
          const allSections = draft?.sections || [];
          const money = (n) => `$${Number(n || 0).toLocaleString('en-US')}`;

          let order = null;
          try { order = buildOrder({ sections: allSections, spots }); }
          catch (e) { order = null; }

          // Fallback: if the order can't be built, still show per-module totals.
          if (!order) {
            const st = buildPricingState(spots);
            const rows = [];
            for (const s of allSections) {
              if (s.type !== 'pricing_table') continue;
              const t = calcTotal(s.rows || [], st);
              if (t) rows.push({ label: s.label || s._module || 'Services', price: t });
            }
            if (!rows.length) return null;
            const sum = rows.reduce((a, r) => a + (parsePrice(r.price) || 0), 0);
            return (
              <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14, color: '#1a3a6e' }}>Pricing Summary</div>
                {rows.map((r, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 14 }}>
                    <span style={{ color: '#333' }}>{r.label}</span><span style={{ fontWeight: 600 }}>{r.price}</span>
                  </div>
                ))}
                {sum > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', fontSize: 15 }}>
                    <span style={{ fontWeight: 700, color: '#1a3a6e' }}>Total</span>
                    <span style={{ fontWeight: 700, color: '#1a3a6e' }}>{money(sum)}</span>
                  </div>
                )}
              </div>
            );
          }

          // Group the selected line items the way the document reads: by pricing
          // table (ConnectEHR, Patient Portal, …), bank hours on their own line.
          const groups = new Map(); // label -> { items: [], subtotal }
          for (const li of order.lineItems) {
            const label = li.kind === 'bank_hours'
              ? 'Prepaid Support Hours'
              : (li.table || li.product || 'Services');
            if (!groups.has(label)) groups.set(label, { items: [], subtotal: 0 });
            const g = groups.get(label);
            g.items.push(li);
            g.subtotal += li.amount || 0;
          }
          if (!groups.size) return null;

          // "3 × $250", "Unlimited", "6 hours × $180/hr"
          const detailFor = (li) => {
            if (li.qty === 'unlimited') return 'Unlimited';
            if (li.kind === 'bank_hours') return `${li.qty} hours × ${money(li.unitPrice)}/hr`;
            if (li.kind === 'quantity') {
              if (!li.qty) return null;
              return li.unitPrice != null ? `${li.qty} × ${money(li.unitPrice)}` : `Qty ${li.qty}`;
            }
            return null;
          };

          const grand = order.totals.grand;
          const year2 = order.totals.year2;

          return (
            <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, color: '#1a3a6e' }}>Pricing Summary</div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>Everything you've selected so far.</div>

              {Array.from(groups.entries()).map(([label, g], gi) => (
                <div key={gi} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0', borderBottom: '1px solid #e6e8f0' }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#1a3a6e' }}>{label}</span>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{money(g.subtotal)}</span>
                  </div>
                  {g.items.map((li, li_i) => {
                    const detail = detailFor(li);
                    return (
                      <div key={li_i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '5px 0 5px 12px', fontSize: 13, borderBottom: '1px solid #f6f6f9' }}>
                        <span style={{ color: '#444' }}>
                          {li.name}
                          {detail && <span style={{ color: '#999', fontSize: 12 }}> — {detail}</span>}
                        </span>
                        <span style={{ color: li.included ? '#888' : '#333', fontWeight: li.included ? 400 : 600, whiteSpace: 'nowrap', fontStyle: li.included ? 'italic' : 'normal' }}>
                          {li.included ? 'Included' : money(li.amount)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', fontSize: 16, borderTop: '2px solid #1a3a6e' }}>
                <span style={{ fontWeight: 700, color: '#1a3a6e' }}>Total</span>
                <span style={{ fontWeight: 700, color: '#1a3a6e' }}>{money(grand)}</span>
              </div>
              {year2 > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0 0', fontSize: 13, color: '#666' }}>
                  <span>Subsequent year (annual)</span>
                  <span style={{ fontWeight: 600 }}>{money(year2)}</span>
                </div>
              )}
              {/* Billing-shape split from the classified catalog. Only rendered when
                  every line is classified AND there's a recurring component — a
                  pure one-time deal's split would just repeat the Total. */}
              {(() => {
                const bb = order.totals.byBilling || {};
                if (bb.unclassified) return null;
                if (!bb.monthly && !bb.annual) return null;
                const parts = [];
                if (bb.one_time) parts.push({ label: 'Due one-time', val: money(bb.one_time) });
                if (bb.monthly)  parts.push({ label: 'Recurring monthly', val: `${money(bb.monthly)}/month` });
                if (bb.annual)   parts.push({ label: 'Recurring annually', val: `${money(bb.annual)}/year` });
                return (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #d5d9e6' }}>
                    {parts.map((p, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13, color: '#666' }}>
                        <span>{p.label}</span>
                        <span style={{ fontWeight: 600, color: '#1a3a6e' }}>{p.val}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {renderSections(signPages[currentPage] || [], draft?.fields || {}, spots, handleSpotClick, pageKeyOffset, handleQtyChange, { onDecline: declineSpot, onUndoDecline: undoDecline })}
        </div>

        {/* Comment sidebar */}
        <div style={{ width: 200, flexShrink: 0, paddingTop: 8 }}>
          {pendingComments.length === 0 ? (
            <div style={{ fontSize: 12, color: '#ccc', textAlign: 'center', paddingTop: 20, lineHeight: 1.7 }}>
              Select text to<br />add a comment
            </div>
          ) : pendingComments.map((c, i) => (
            <div key={i} style={{ background: '#fff8e1', border: '1px solid #fcd34d', borderRadius: 6, padding: '10px 12px', marginBottom: 8, fontSize: 12, position: 'relative' }}>
              <div style={{ borderLeft: '3px solid #F9A825', paddingLeft: 8, marginBottom: 6, color: '#795548', fontStyle: 'italic', fontSize: 11, lineHeight: 1.5 }}>
                "{c.sectionPreview.slice(0, 80)}{c.sectionPreview.length > 80 ? '…' : ''}"
              </div>
              <div style={{ color: '#333', lineHeight: 1.5, marginRight: 14 }}>{c.text}</div>
              <button
                onClick={() => setPendingComments(p => p.filter((_, j) => j !== i))}
                style={{ position: 'absolute', top: 6, right: 6, background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 2 }}
                onMouseEnter={e => e.currentTarget.style.color = '#c62828'}
                onMouseLeave={e => e.currentTarget.style.color = '#ccc'}
              >✕</button>
            </div>
          ))}
        </div>
      </div>

      {/* Prompt to select at least one product before submitting */}
      {isLastPage && !atLeastOneProduct && (
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 16px' }}>
          <div style={{ background: '#fff8e1', border: '1px solid #fcd34d', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#795548' }}>
            You haven’t initialed any product yet. Go back and initial at least one product to continue — optional add-ons can be left as “No thanks.”
          </div>
        </div>
      )}

      {/* Page navigation */}
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '16px 16px 60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button
          onClick={() => { setCurrentPage(p => p - 1); window.scrollTo(0, 0); }}
          disabled={currentPage === 0}
          style={{ ...btnStyle('#fff', '#ddd', '#555'), opacity: currentPage === 0 ? 0.3 : 1 }}
        >
          ← Previous
        </button>
        <span style={{ fontSize: 13, color: '#888' }}>Page {currentPage + 1} of {totalPages}</span>
        {isLastPage ? (
          pendingComments.length > 0 ? (
            <button
              onClick={handleSubmitComments}
              disabled={submitting}
              style={{ ...btnStyle('#e07b3f', '#e07b3f', '#fff'), fontWeight: 600 }}
            >
              {submitting ? 'Submitting…' : `Submit ${pendingComments.length} Comment${pendingComments.length !== 1 ? 's' : ''} →`}
            </button>
          ) : (
            <button
              disabled={!allFilled || submitting}
              onClick={handleSubmit}
              style={{ ...btnStyle(allFilled ? '#1a6e3c' : '#ccc', allFilled ? '#1a6e3c' : '#ccc', '#fff'), fontWeight: 600, opacity: (!allFilled || submitting) ? 0.5 : 1 }}
            >
              {submitting ? 'Submitting…' : 'Submit & Sign →'}
            </button>
          )
        ) : (
          <button
            onClick={() => { setCurrentPage(p => p + 1); window.scrollTo(0, 0); }}
            style={btnStyle('#1a3a6e', '#1a3a6e', '#fff')}
          >
            Next →
          </button>
        )}
      </div>

      {/* Jump to next spot — floating button, hidden when all spots on page are filled */}
      {!allFilled && (
        <button
          onClick={jumpToNextSpot}
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 200,
            background: unfilledOnPage > 0 ? '#F9A825' : '#1a3a6e',
            color: '#fff', border: 'none', borderRadius: 28,
            padding: '12px 22px', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          {unfilledOnPage > 0
            ? `Next spot (${unfilledOnPage} left) ↓`
            : `Next page →`}
        </button>
      )}

      {/* Text prompt modal */}
      {prompt && (
        <TextPrompt
          field={prompt.field}
          onSave={(val) => { fillSpot(prompt.spotId, val); setPrompt(null); }}
          onClose={() => setPrompt(null)}
        />
      )}

      {/* Selection → "Add comment" button */}
      {selectionAnchor && (
        <button
          onMouseDown={e => e.preventDefault()}
          onClick={() => { setCommentPopover(selectionAnchor); setSelectionAnchor(null); }}
          style={{
            position: 'fixed',
            top: selectionAnchor.y - 38,
            left: selectionAnchor.x,
            zIndex: 400,
            background: '#1a3a6e', color: '#fff', border: 'none',
            borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', gap: 5,
          }}
        >
          💬 Add comment
        </button>
      )}

      {/* Comment popover */}
      {commentPopover && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 500 }}
          onMouseDown={() => { setCommentPopover(null); setCommentPopoverText(''); }}
        >
          <div
            style={{
              position: 'fixed',
              top: Math.min(commentPopover.y + 8, window.innerHeight - 270),
              left: Math.min(commentPopover.x, window.innerWidth - 320),
              width: 300,
              background: '#fff', borderRadius: 8,
              boxShadow: '0 4px 24px rgba(0,0,0,0.18)', padding: 16, zIndex: 501,
            }}
            onMouseDown={e => e.stopPropagation()}
          >
            {commentPopover.selectedText && (
              <div style={{ borderLeft: '3px solid #F9A825', paddingLeft: 10, marginBottom: 12, fontSize: 12, color: '#888', fontStyle: 'italic', lineHeight: 1.5 }}>
                "{commentPopover.selectedText.slice(0, 120)}{commentPopover.selectedText.length > 120 ? '…' : ''}"
              </div>
            )}
            <textarea
              autoFocus
              value={commentPopoverText}
              onChange={e => setCommentPopoverText(e.target.value)}
              onKeyDown={e => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') addPendingComment();
                if (e.key === 'Escape') { setCommentPopover(null); setCommentPopoverText(''); }
              }}
              placeholder="Add a comment… (Ctrl+Enter to save)"
              rows={3}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 13, resize: 'vertical', fontFamily: 'Arial, sans-serif', marginBottom: 10 }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setCommentPopover(null); setCommentPopoverText(''); }}
                style={{ padding: '6px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#555' }}
              >
                Cancel
              </button>
              <button
                onClick={addPendingComment}
                disabled={!commentPopoverText.trim()}
                style={{ padding: '6px 14px', background: '#1a3a6e', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: commentPopoverText.trim() ? 'pointer' : 'not-allowed', color: '#fff', opacity: commentPopoverText.trim() ? 1 : 0.5 }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Section key counter (for pagination offset) ──────────────────────────────

function countSectionKeys(section) {
  if (section.type === 'pricing_table') {
    return (section.rows || []).reduce((n, r) => {
      if (r.qty) return n + 1 + (r.unlimited ? 1 : 0);
      return n + (r.initial ? 1 : 0);
    }, 0);
  }
  if (section.type === 'bank_of_hours') return 1;
  if (section.type === 'signature') return 9;
  return 0;
}

// ─── Section Renderer ─────────────────────────────────────────────────────────

function renderSections(sections, fields, spots, onSpotClick, keyOffset = 0, onQtyChange = () => {}, { onDecline = () => {}, onUndoDecline = () => {} } = {}) {
  let sectionIndex = 0;

  const spotKeys = Object.keys(spots);
  let keyIdx = keyOffset;

  const getNextKey = () => spotKeys[keyIdx++] || null;

  // Grand-total amounts for pricing_total sections on this page — computed live
  // from the client's selections, so the figure updates as they initial rows.
  const pageGrandTotals = grandTotalsByIndex(sections, buildPricingState(spots));

  // Renders the accept/decline control for an initial row: filled stamp (click to clear),
  // an explicit "Declined" state (with undo), a lock when gated, or Initial + "No thanks".
  const renderInitialCell = (key, spot, locked) => {
    if (!spot) return null;
    if (spot.filled) {
      return <img src={spot.value} alt="initials" title="Click to clear" onClick={() => onSpotClick(key)} style={{ height: 32, maxWidth: 80, cursor: 'pointer' }} />;
    }
    if (spot.declined) {
      return (
        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <span style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>Declined</span>
          <span onClick={() => onUndoDecline(key)} style={{ fontSize: 10, color: '#1565C0', cursor: 'pointer', textDecoration: 'underline' }}>undo</span>
        </div>
      );
    }
    if (locked) {
      return <SpotButton filled={false} onClick={() => {}} type="initial" label="Initial" spotId={key} locked />;
    }
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <SpotButton filled={false} onClick={() => onSpotClick(key)} type="initial" label="Initial" spotId={key} />
        <span onClick={() => onDecline(key)} title="Decline this item" style={{ fontSize: 11, color: '#999', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}>No&nbsp;thanks</span>
      </div>
    );
  };

  const elements = [];

  for (const section of sections) {
    const content = fill(section.content || '', fields);

    switch (section.type) {
      case 'doc_header':
        elements.push(
          <div key={sectionIndex} style={{ borderBottom: '3px solid #2872FA', paddingBottom: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 16, fontStyle: 'italic' }}>Dynamic Health IT, Inc.</div>
            <img src="/api/logo-d" alt="DHIT" style={{ width: 32, height: 32, objectFit: 'contain' }}
              onError={e => { e.target.style.display = 'none'; }} />
          </div>
        );
        break;

      case 'title_italic':
        elements.push(<div key={sectionIndex} style={{ textAlign: 'center', fontSize: 20, fontWeight: 700, fontStyle: 'italic', marginBottom: 6 }}>{content}</div>);
        break;

      case 'heading':
        elements.push(<div key={sectionIndex} style={{ fontSize: 15, fontWeight: 700, textDecoration: 'underline', marginTop: 20, marginBottom: 8 }}>{content}</div>);
        break;

      case 'diamond_bullet':
        // marginTop gives every main bullet a consistent gap above it (collapses with the
        // previous element's margin, so sub-bullets stay tight to their parent).
        elements.push(<div key={sectionIndex} style={{ paddingLeft: 20, marginTop: 9, marginBottom: 6, fontSize: 13, lineHeight: 1.6 }}>❖ &nbsp;{content}</div>);
        break;

      case 'arrow_bullet':
        elements.push(<div key={sectionIndex} style={{ paddingLeft: 48, marginBottom: 4, fontSize: 13, lineHeight: 1.6 }}>➢ &nbsp;{content}</div>);
        break;

      case 'square_bullet':
        elements.push(<div key={sectionIndex} style={{ paddingLeft: 76, marginBottom: 4, fontSize: 13, lineHeight: 1.6 }}>■ &nbsp;{content}</div>);
        break;

      case 'bullet':
        elements.push(<div key={sectionIndex} style={{ paddingLeft: 20, marginBottom: 4, fontSize: 13, lineHeight: 1.6 }}>• &nbsp;{content}</div>);
        break;

      case 'paragraph':
        elements.push(<p key={sectionIndex} style={{ margin: '0 0 8px', textIndent: 24, fontSize: 13, lineHeight: 1.7 }}>{content}</p>);
        break;

      case 'paragraph_noindent':
        elements.push(<p key={sectionIndex} style={{ margin: '0 0 8px', fontSize: 13, lineHeight: 1.7 }}>{content}</p>);
        break;

      case 'title':
        elements.push(<div key={sectionIndex} style={{ textAlign: 'center', fontSize: 22, fontWeight: 700, marginBottom: 6 }}>{content}</div>);
        break;

      case 'subtitle':
        elements.push(<div key={sectionIndex} style={{ textAlign: 'center', fontSize: 16, fontWeight: 600, color: '#555', marginBottom: 6 }}>{content}</div>);
        break;

      case 'note': {
        // Optional indent (1=diamond, 2=arrow, 3=square) so asterisk footnotes
        // align under the bullets they annotate. Matches bullet paddingLeft scale.
        const noteIndent = { 1: 20, 2: 48, 3: 76 }[section.indent] || 0;
        elements.push(<div key={sectionIndex} style={{ fontSize: 11, color: '#888', fontStyle: 'italic', margin: '4px 0', paddingLeft: noteIndent, lineHeight: 1.5 }}>{content}</div>);
        break;
      }

      case 'hr':
        elements.push(<hr key={sectionIndex} style={{ border: 'none', borderTop: '1px solid #ddd', margin: '12px 0' }} />);
        break;

      case 'pricing_total':
        elements.push(<div key={sectionIndex} style={{ fontWeight: 700, fontSize: 14, padding: '8px 0', margin: '12px 0', borderTop: '2px solid #000', borderBottom: '2px solid #000' }}>{fillPricingTotal(content, pageGrandTotals[sectionIndex] || 0)}</div>);
        break;

      case 'bank_of_hours': {
        const rate     = section.rate || 180;
        const minHours = section.minHours || 5;
        const bankKey  = getNextKey();
        const bankSpot = bankKey ? spots[bankKey] : null;
        const hours    = bankSpot && bankSpot.value != null ? bankSpot.value : null;
        const cost     = hours ? hours * rate : 0;
        const belowMin = hours != null && hours > 0 && hours < minHours;
        elements.push(
          <div key={sectionIndex} style={{ border: '1px solid #d8dcee', borderRadius: 8, background: '#f8f9ff', padding: '14px 16px', margin: '12px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span aria-hidden="true">⏱</span>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#1a3a6e' }}>Prepaid Bank of Support Hours</span>
              <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 15, color: '#1a3a6e' }}>${rate}<span style={{ fontSize: 11, fontWeight: 400 }}>/hr</span></span>
            </div>
            {bankSpot && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 8px' }}>
                <span style={{ fontSize: 13, color: '#333' }}>Hours:</span>
                <input
                  type="number"
                  min={minHours}
                  inputMode="numeric"
                  value={bankSpot.value ?? ''}
                  onChange={(e) => onQtyChange(bankKey, e.target.value)}
                  placeholder="0"
                  style={{
                    width: 70, textAlign: 'center', fontSize: 13, padding: '4px 6px',
                    borderRadius: 4, border: `1.5px solid ${hours ? '#2e7d32' : '#aaa'}`,
                    background: hours ? '#E8F5E9' : '#fff', color: hours ? '#2e7d32' : '#333',
                  }}
                />
                <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 15, color: cost ? '#1a3a6e' : '#aaa' }}>
                  ${cost.toLocaleString('en-US')}
                </span>
              </div>
            )}
            <div style={{ fontSize: 12, color: belowMin ? '#c0392b' : '#888' }}>
              {minHours}-hour minimum &nbsp;·&nbsp; Hours never expire &nbsp;·&nbsp; No refunds
            </div>
          </div>
        );
        break;
      }

      case 'data_table': {
        const headers = section.headers || [];
        const rows    = section.rows || [];
        const renderCell = (cell) =>
          Array.isArray(cell)
            ? <ul style={{ margin: 0, paddingLeft: 16 }}>{cell.map((line, li) => <li key={li} style={{ marginBottom: 2 }}>{line}</li>)}</ul>
            : cell;
        elements.push(
          <div key={sectionIndex} style={{ margin: '12px 0' }}>
            {section.label && <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{section.label}</div>}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, border: '1px solid #999' }}>
              {headers.length > 0 && (
                <thead><tr style={{ background: '#D9E2F3' }}>
                  {headers.map((h, hi) => <th key={hi} style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid #999', fontWeight: 700 }}>{h}</th>)}
                </tr></thead>
              )}
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    {(row.cells || []).map((c, ci) => (
                      <td key={ci} style={{ padding: '6px 8px', border: '1px solid #999', verticalAlign: 'top' }}>{renderCell(c)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        break;
      }

      case 'spacer':
        elements.push(<div key={sectionIndex} style={{ height: 20 }} />);
        break;

      case 'pricing_table': {
        const rows = section.rows || [];
        const pricingState = buildPricingState(spots);
        const autoTotal = calcTotal(rows, pricingState);

        // Optional add-ons are locked until the table's main/core product is initialed.
        const coreRow = rows.find(r => r.initial && !r.optional && !r.isTotal);
        const coreSelected = coreRow ? !!(pricingState[coreRow.id] && pricingState[coreRow.id].selected) : true;

        // Dual-year vendor table: Description | Initial | Year 1 | Year 2, with a
        // separate computed total per year column.
        if (section.dualYear) {
          const labels = section.yearLabels || ['Year 1', 'Year 2'];
          const totY1 = calcTotalYear(rows, pricingState, 1);
          const totY2 = calcTotalYear(rows, pricingState, 2);
          elements.push(
            <div key={sectionIndex} style={{ marginBottom: 16 }}>
              {section.label && <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{section.label}</div>}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#1B1464', color: '#fff' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', width: '46%' }}>Module / Description</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', width: '16%' }}>Initial Here</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', width: '19%' }}>{labels[0]}</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', width: '19%' }}>{labels[1]}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => {
                    let initialKey = null;
                    if (row.qty) { getNextKey(); if (row.unlimited) getNextKey(); }
                    else if (row.initial) { initialKey = getNextKey(); }
                    const initialSpot = initialKey ? spots[initialKey] : null;
                    const isTot = row.isTotal;
                    const y2tot = row.totalYear === 2;
                    const cell1 = isTot ? (y2tot ? '' : (totY1 || '')) : (row.price || '');
                    const cell2 = isTot ? (y2tot ? (totY2 || '') : '') : (row.price2 || '');
                    return (
                      <tr key={ri} style={{ background: isTot ? '#E8E8F0' : ri % 2 === 0 ? '#fff' : '#f9f9f9', borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px 10px', fontWeight: isTot ? 700 : 400 }}>
                          {row.module}
                          {row.items && row.items.map((item, ii) => (
                            <div key={ii} style={{ fontSize: 11, color: '#666', paddingLeft: 12, marginTop: 2 }}>{item}</div>
                          ))}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                          {renderInitialCell(initialKey, initialSpot, false)}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: isTot ? 700 : 400 }}>{cell1}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: isTot ? 700 : 400 }}>{cell2}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
          break;
        }
        elements.push(
          <div key={sectionIndex} style={{ marginBottom: 16 }}>
            {section.label && <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{section.label}</div>}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#1B1464', color: '#fff' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', width: '55%' }}>Module / Description</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', width: '25%' }}>Initial Here</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', width: '20%' }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => {
                  let qtyKey = null, unlKey = null, initialKey = null;
                  if (row.qty) {
                    qtyKey = getNextKey();
                    if (row.unlimited) unlKey = getNextKey();
                  } else if (row.initial) {
                    initialKey = getNextKey();
                  }
                  const qtySpot = qtyKey ? spots[qtyKey] : null;
                  const unlSpot = unlKey ? spots[unlKey] : null;
                  const initialSpot = initialKey ? spots[initialKey] : null;
                  const displayPrice = rowDisplayPrice(row, pricingState[row.id], autoTotal);
                  // Optional add-on locked until the table's core product is initialed.
                  const gated = !!(row.optional && coreRow && !coreSelected);
                  return (
                    <tr key={ri} style={{ background: row.isTotal ? '#E8E8F0' : ri % 2 === 0 ? '#fff' : '#f9f9f9', borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px 10px', fontWeight: row.isTotal ? 700 : 400 }}>
                        {row.module}
                        {row.items && row.items.map((item, ii) => (
                          <div key={ii} style={{ fontSize: 11, color: '#666', paddingLeft: 12, marginTop: 2 }}>{item}</div>
                        ))}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        {qtySpot && (() => {
                          const unlimitedChosen = !!(unlSpot && unlSpot.filled);
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ fontSize: 11, color: '#888' }}>Qty</span>
                                <input
                                  type="number"
                                  min="0"
                                  inputMode="numeric"
                                  value={qtySpot.value ?? ''}
                                  disabled={unlimitedChosen || gated}
                                  title={gated ? 'Initial the main product above to unlock this option' : undefined}
                                  onChange={(e) => onQtyChange(qtyKey, e.target.value)}
                                  placeholder="0"
                                  style={{
                                    width: 56, textAlign: 'center', fontSize: 13, padding: '3px 6px',
                                    borderRadius: 4, border: `1.5px solid ${gated ? '#ccc' : qtySpot.filled ? '#2e7d32' : '#aaa'}`,
                                    background: (unlimitedChosen || gated) ? '#f0f0f0' : qtySpot.filled ? '#E8F5E9' : '#fff',
                                    color: gated ? '#aaa' : qtySpot.filled ? '#2e7d32' : '#333',
                                    cursor: gated ? 'not-allowed' : 'auto',
                                  }}
                                />
                              </div>
                              {unlSpot && (
                                <>
                                  <span style={{ fontSize: 10, color: '#999' }}>or</span>
                                  {unlSpot.filled ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                      <img src={unlSpot.value} alt="initials" style={{ height: 28, maxWidth: 70 }} />
                                      <span
                                        onClick={() => onSpotClick(unlKey)}
                                        style={{ fontSize: 10, color: '#c0392b', cursor: 'pointer', textDecoration: 'underline' }}
                                      >
                                        clear unlimited
                                      </span>
                                    </div>
                                  ) : (
                                    <SpotButton filled={false} onClick={() => onSpotClick(unlKey)} type="initial" label="Unlimited" spotId={unlKey} locked={gated} />
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })()}
                        {renderInitialCell(initialKey, initialSpot, gated)}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: row.isTotal ? 700 : 400 }}>{displayPrice}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
        break;
      }

      case 'signature': {
        const sigSign  = getNextKey();
        const sigName  = getNextKey();
        const sigTitle = getNextKey();
        const sigDate  = getNextKey();
        const sigAddr1 = getNextKey();
        const sigAddr2 = getNextKey();
        const apName   = getNextKey();
        const apPhone  = getNextKey();
        const apEmail  = getNextKey();

        const S = (key, type) => {
          const spot = spots[key];
          if (!spot) return null;
          if (spot.filled) {
            if (type === 'signature' || type === 'initial') {
              return <img src={spot.value} alt={spot.label} style={{ height: type === 'signature' ? 48 : 36, maxWidth: 180 }} />;
            }
            return <span style={{ color: '#1a3a6e', fontWeight: 500 }}>{spot.value}</span>;
          }
          return <SpotButton filled={false} onClick={() => onSpotClick(key)} type={type} label={spot.label} spotId={key} />;
        };

        elements.push(
          <div key={sectionIndex} style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid #ddd' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              {/* Client column */}
              <div>
                <div style={{ fontWeight: 700, marginBottom: 12 }}>Client</div>
                <div style={sigRow}>By (Signature): &nbsp;{S(sigSign, 'signature')}</div>
                <div style={sigRow}>Print Name: &nbsp;{S(sigName, 'text')}</div>
                <div style={sigRow}>Title: &nbsp;{S(sigTitle, 'text')}</div>
                <div style={sigRow}>Date: &nbsp;{S(sigDate, 'text')}</div>
                <div style={{ ...sigRow, marginTop: 8 }}>Mailing Address:</div>
                <div style={sigRow}>&nbsp;&nbsp;{S(sigAddr1, 'text')}</div>
                <div style={sigRow}>&nbsp;&nbsp;{S(sigAddr2, 'text')}</div>
              </div>
              {/* DHIT column */}
              <div>
                <div style={{ fontWeight: 700, marginBottom: 12 }}>Dynamic Health IT, Inc.</div>
                <div style={sigRow}>By: ____________________________</div>
                <div style={sigRow}>Jeffery P. Robbins</div>
                <div style={sigRow}>President</div>
                <div style={{ ...sigRow, marginTop: 8 }}>Date: _______________________</div>
              </div>
            </div>
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #eee' }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Client A/P Contact</div>
              <div style={sigRow}>Name: &nbsp;{S(apName, 'text')}</div>
              <div style={sigRow}>Phone: &nbsp;{S(apPhone, 'text')}</div>
              <div style={sigRow}>Email: &nbsp;{S(apEmail, 'text')}</div>
            </div>
          </div>
        );
        break;
      }

      default: break;
    }

    sectionIndex++;
  }

  return <div style={{ background: '#fff', borderRadius: 10, padding: 32, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>{elements}</div>;
}

// ─── Shared styles & helpers ──────────────────────────────────────────────────

const pageWrap = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#f4f5f7', fontFamily: 'Arial, sans-serif', padding: 20,
};

const card = (maxW) => ({
  background: '#fff', borderRadius: 12, padding: '40px 36px',
  width: '100%', maxWidth: maxW, boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
});

const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid #ddd',
  borderRadius: 6, fontSize: 14, boxSizing: 'border-box', marginBottom: 12,
};

const confirmLabel = {
  display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4, marginTop: 4,
};

const errorBox = {
  background: '#fdecea', color: '#c62828', border: '1px solid #f5c6cb',
  borderRadius: 6, padding: '10px 14px', fontSize: 13, marginBottom: 12,
};

const sigRow = {
  fontSize: 13, padding: '5px 0', borderBottom: '1px solid #f0f0f0',
  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, minHeight: 32,
};

function Logo() {
  return (
    <div style={{ textAlign: 'center', marginBottom: 20 }}>
      <img src="/logo.png" alt="DHIT" style={{ height: 48, objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
    </div>
  );
}

function fill(text, data) {
  if (!text) return '';
  return text.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const val = data[key.toUpperCase()];
    return val !== undefined ? val : `{{${key}}}`;
  });
}
